import dayjs from 'dayjs';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/**
 * 将数字金额分解为各个位数的中文大写
 */
const parseCurrencyDigits = (amount) => {
  if (!amount || isNaN(amount)) {
    return {
      wan: '零',
      qian: '零',
      bai: '零',
      shi: '零',
      yuan: '零',
      xiaoxie: '0.00'
    };
  }
  
  const digits = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];
  const totalAmount = parseFloat(amount);
  const xiaoxie = totalAmount.toFixed(2);
  const yuanPart = Math.floor(totalAmount);
  
  // 将元部分转换为5位字符串，不足前面补零
  const yuanStr = yuanPart.toString().padStart(5, '0');
  
  // 分解各个位数
  return {
    wan: digits[parseInt(yuanStr[0])],  // 万位
    qian: digits[parseInt(yuanStr[1])], // 仟位
    bai: digits[parseInt(yuanStr[2])],  // 佰位
    shi: digits[parseInt(yuanStr[3])],  // 拾位
    yuan: digits[parseInt(yuanStr[4])], // 圆位
    xiaoxie: xiaoxie
  };
};

export const exportSalesOrderToExcel = async (orderId, api) => {
  try {
    // 1. 获取订单数据
    const response = await api.get(`/v1/sales_orders/${orderId}`);
    const order = response.data;
    const items = order.sales_items || [];
    
    // 2. 加载模板文件
    const workbook = new ExcelJS.Workbook();
    const templateResponse = await fetch('/templates/发货单.xlsx');
    const templateArrayBuffer = await templateResponse.arrayBuffer();
    await workbook.xlsx.load(templateArrayBuffer);
    
    const worksheet = workbook.getWorksheet(1);
    
    // 3. 填充订单信息
    // 订单日期 - 第2行A-H列合并单元格
    worksheet.getCell('A2').value = dayjs(order.order_date).format('YYYY.MM.DD');
    
    // 客户信息
    worksheet.getCell('A3').value = order.customer?.contact_person || '';
    worksheet.getCell('B3').value = order.delivery_address || '';
    worksheet.getCell('G3').value = order.customer?.phone || '';
    
    // 4. 填充订单明细
    items.forEach((item, index) => {
      const rowIndex = 6 + index; // 从第6行开始
      if (rowIndex > 18) return; // 只填充到第18行
      
      worksheet.getCell(`A${rowIndex}`).value = item.product?.name || '';
      worksheet.getCell(`B${rowIndex}`).value = item.product?.spec || ''; // 产品的规格
      worksheet.getCell(`C${rowIndex}`).value = item.product?.unit || ''; // 产品的单位
      worksheet.getCell(`D${rowIndex}`).value = parseFloat(item.quantity || 0);
      worksheet.getCell(`E${rowIndex}`).value = parseFloat(item.unit_price || 0);
      worksheet.getCell(`F${rowIndex}`).value = parseFloat(item.amount || 0);
    });
    
    // 5. 填充合计金额
    worksheet.getCell('F19').value = parseFloat(order.total_amount || 0);
    
    // 6. 填充欠条部分（使用简化的富文本格式）
    const currencyDigits = parseCurrencyDigits(order.total_amount || 0);
    
    // 设置到G5单元格（整个合并区域的左上角）
    const receiptCell = worksheet.getCell('G5');
    
    // 使用简化的富文本格式，明确设置每个片段的字体大小
    receiptCell.value = {
      richText: [
        // 标题：欠条（加粗）
        { text: '              欠  条\n\n', font: { bold: true, name: '宋体', size: 8 } },
        // 正文部分
        { text: '   今欠到湖北鹏晖新型建材有限公司\n\n', font: { name: '宋体', size: 8 } },
        // 人民币大写
        { text: ' 人民币（大写）', font: { name: '宋体', size: 8 } },
        { text: currencyDigits.wan, font: { underline: 'single', name: '宋体', size: 8 } },
        { text: ' 万 ', font: { name: '宋体', size: 8 } },
        { text: currencyDigits.qian, font: { underline: 'single', name: '宋体', size: 8 } },
        { text: ' 仟 ', font: { name: '宋体', size: 8 } },
        { text: currencyDigits.bai, font: { underline: 'single', name: '宋体', size: 8 } },
        { text: ' 佰  ', font: { name: '宋体', size: 8 } },
        { text: currencyDigits.shi, font: { underline: 'single', name: '宋体', size: 8 } },
        { text: ' 拾 ', font: { name: '宋体', size: 8 } },
        { text: currencyDigits.yuan, font: { underline: 'single', name: '宋体', size: 8 } },
        { text: ' 圆', font: { name: '宋体', size: 8 } },
        { text: ' ¥ ', font: { bold: true, name: '宋体', size: 8 } },
        { text: currencyDigits.xiaoxie, font: { underline: 'single', name: '宋体', size: 8 } },
        { text: ' 元\n\n', font: { name: '宋体', size: 8 } },
        // 欠款单位经办人
        { text: ' 欠款单位经办人：  ', font: { name: '宋体', size: 8 } },
        { text: order.customer?.contact_person || '', font: { underline: 'single', name: '宋体', size: 8 } },
        { text: '\n\n', font: { name: '宋体', size: 8 } },
        // 身份证号码
        { text: ' 身份证号码：', font: { name: '宋体', size: 8 } },
        { text: '', font: { underline: 'single', name: '宋体', size: 8 } },
        { text: '\n\n', font: { name: '宋体', size: 8 } },
        // 住址
        { text: ' 住址： ', font: { name: '宋体', size: 8 } },
        { text: order.customer?.address || '', font: { underline: 'single', name: '宋体', size: 8 } },
        { text: '\n\n', font: { name: '宋体', size: 8 } },
        // 电话
        { text: ' 电话： ', font: { name: '宋体', size: 8 } },
        { text: order.customer?.phone || '', font: { underline: 'single', name: '宋体', size: 8 } },
        { text: '\n\n', font: { name: '宋体', size: 8 } },
        // 双方约定
        { text: ' 双方约定：\n', font: { name: '宋体', size: 8 } },
        { text: ' 1.货到验收卸货；\n', font: { name: '宋体', size: 8 } },
        { text: ' 2.如发生纠纷在卖方所在地法院处理。', font: { name: '宋体', size: 8 } }
      ]
    };
    
    // 在单元格级别设置对齐方式
    receiptCell.alignment = {
      vertical: 'top',
      horizontal: 'left',
      wrapText: true
    };
    
    // 设置基本字体样式
    receiptCell.font = {
      name: '宋体',
      size: 8
    };
    
    // 7. 生成并下载Excel文件
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `销售订单_${order.order_no}.xlsx`);
  } catch (error) {
    console.error('导出销售订单失败:', error);
    throw error;
  }
};


